import {convertCatalogNumericFields} from '../shared/catalogNumericFields.js';

export function useCatalog(opts = {}) {
	const nuxtApp = useNuxtApp();
	const endpoints = useEndpoints();
	const appConfig = useAppConfig();
	const route = opts?.route || useRoute();
	const {absoluteImagePath} = useText();
	const {getLastUrlSegment, getUrlSegments} = useUrl();

	const category = useState('catalogCategory', () => null);
	const comparedProducts = useState('comparedProducts', () => []);
	const wishlistProducts = useState('wishlistProducts', () => []);
	const product = useState('product', () => null);

	async function fetchCategoryBySlug(options, config = {}) {
		let slug = options?.slug ? options.slug : route.path;
		const removeLast = __FILTER_URL_TYPE__ == 3 && slug.includes(':');
		slug = getUrlSegments(slug, {ignoreLang: true, removeLast: removeLast, offset: __CATALOG_SEO_URL__ || __CATEGORY_WITHOUT_BASE_URL__ ? 0 : 1, stringify: true});
		if (!slug) return null; // do not fetch category if slug is not provided

		const fetchOptions = {
			...options,
			slug: slug,
			mode: 'full',
			single: true,
		};

		const res = await useApi(
			'/api/nuxtapi/catalog/categories/',
			{
				method: 'POST',
				body: fetchOptions,
			},
			{cache: true, key: options, ...config},
		);

		category.value = res?.data?.length ? res.data[0] : null;
		return res;
	}

	// fetch categories
	async function fetchCategories(options, config = {}) {
		return await useApi(
			'/api/nuxtapi/catalog/categories/',
			{
				method: 'POST',
				body: options,
			},
			{cache: true, key: options, ...config},
		);
	}

	// fetch products
	async function fetchProducts(options, config = {}) {
		const fetchOptions = {...options};
		if (fetchOptions.a === null || String(fetchOptions.a).trim().toLowerCase() === 'null') delete fetchOptions.a;

		// Use hapi endpoint if special view is enabled (for single user last viewed products)
		const fetchUrl = config?.cache == false && fetchOptions.special_view ? endpoints.get('_post_hapi_catalog_products') : '/api/nuxtapi/catalog/products/';
		return await useApi(
			fetchUrl,
			{
				method: 'POST',
				body: fetchOptions,
			},
			{cache: true, key: fetchOptions, ...config},
		);
	}

	// fetch products price and qty (for users with special prices)
	async function fetchProductsPriceQty(options, config = {}) {
		const response = await useApi(
			endpoints.get('_post_hapi_catalog_products_price_qty'),
			{
				method: 'POST',
				body: options,
			},
			{cache: true, key: options, ...config},
		);

		if (response?.data?.items?.length) response.data.items.forEach(convertCatalogNumericFields);
		return response;
	}

	// process product data with price and qty
	async function updateProductsPriceQty(data) {
		// if data is object (single product)
		if (!Array.isArray(data) && data.id) {
			const productPrices = await fetchProductsPriceQty({ids: [data.id]});
			if (productPrices?.data?.items?.length) {
				data = {...data, ...productPrices.data.items[0], priceQtyLoaded: true};
			}
			return data;
		}

		// if data is array (product lists)
		if (Array.isArray(data) && data.length) {
			const productPrices = await fetchProductsPriceQty({ids: data.map(el => el.id)});

			let tempItems = [];
			if (productPrices?.data?.items?.length) {
				tempItems = data.map(el => {
					const product = productPrices.data.items.find(p => p.id == el.id);
					if (product) {
						el = {...el, ...product, priceQtyLoaded: true};
					}
					return el;
				});
			}
			return tempItems.length ? tempItems : data;
		}
	}

	// fetch lists
	async function fetchLists(options, config = {}) {
		return useApi(
			'/api/nuxtapi/catalog/lists/',
			{
				method: 'POST',
				body: options,
			},
			{cache: true, key: options, ...config},
		);
	}

	async function fetchListBySlug(options, config = {}) {
		let url = options?.slug ? options.slug : route.path;
		if (__FILTER_URL_TYPE__ == 3 && url.includes(':')) {
			url = getUrlSegments(url, {ignoreLang: true, removeLast: true, stringify: true});
		}
		const slug = getUrlSegments(url)?.length > 2 ? getUrlSegments(url, {offset: 1, stringify: true, ignoreLang: true}) : getLastUrlSegment(url);
		const fetchOptions = {
			slug: slug,
			limit: 1,
			mode: 'full',
			single: true,
		};
		if (appConfig?.catalog?.listsResponseFields?.length) fetchOptions.response_fields = appConfig.catalog.listsResponseFields;

		const res = await useApi(
			'/api/nuxtapi/catalog/lists/',
			{
				method: 'POST',
				body: fetchOptions,
			},
			{cache: true, key: fetchOptions, ...config},
		);

		category.value = res?.data?.length ? res.data[0] : null;
		return res;
	}

	// fetch sellers
	async function fetchSellers(options, config = {}) {
		return await useApi(
			'/api/nuxtapi/catalog/sellers/',
			{
				method: 'POST',
				body: options,
			},
			{cache: true, key: options, ...config},
		);
	}

	// fetch product
	async function fetchProduct(options, config = {}) {
		const url = options?.item_slug ? options.item_slug : route.path;
		const slug = getLastUrlSegment(url);

		// get product id and slug from url
		const productUrl = slug.split('-');
		const productId = productUrl[productUrl.length - 1];
		const productSlug = productUrl.slice(0, __PRODUCT_URL_TYPE__ == 2 ? -1 : -2).join('-');

		// set fetch options
		const fetchOptions = {
			...options,
			item_slug: productSlug,
			item_id: options?.item_id ? options.item_id : productId,
		};

		let data = await useApi(`/api/nuxtapi/catalog/product/`, {method: 'POST', body: fetchOptions}, {cache: true, key: fetchOptions, ...config}).then(res => (res?.data ? res.data : null));

		// keep the same state data if redirect url is the same as current route
		if (data?.redirect_url_without_domain && data.redirect_url_without_domain == route.fullPath && product.value) {
			return product.value;
		}

		// convert relative /upload/ image paths to absolute
		if (data?.content) data.content = absoluteImagePath(data.content);

		// callback function to modify data
		if (appConfig?.catalog?.hooks?.mapProduct) {
			data = await appConfig.catalog.hooks.mapProduct(data, route);
		}

		// save product to state. This is needed to avoid duplicate fetching on product page
		product.value = data;
		return data;
	}

	function getProductDetail() {
		return product.value;
	}

	// fetch manufacturers
	async function fetchManufacturers(options, config = {}) {
		const fetchOptions = {
			...options,
		};

		// deconstruct id parameter if passed as comma separated string
		if (options?.id && typeof options.id == 'string') {
			const ids = options.id?.split(',');
			fetchOptions.id = ids.filter(el => el != '');
		}

		const res = await useApi(
			'/api/nuxtapi/catalog/manufacturers/',
			{
				method: 'POST',
				body: fetchOptions,
				...config,
			},
			{cache: true, key: fetchOptions, ...config},
		);

		return res;
	}

	async function fetchWishlistProducts(options, config = {}) {
		const res = await useApi(`${endpoints.get('_get_hapi_catalog_my_wishlist')}`, null, {appendLang: true, ...config});
		const items = res.data?.items?.length ? res.data.items : [];

		if (config?.appendToState) {
			if (!wishlistProducts.value) wishlistProducts.value = res?.data;
			const appendItems = [...toRaw(wishlistProducts.value.items), ...items];
			wishlistProducts.value = {...wishlistProducts.value, items: appendItems, pagination: res.data?.pagination};
		} else {
			wishlistProducts.value = res?.data;
		}

		return res;
	}

	// Remove all wishlist products
	async function removeWishlistProducts(options, config = {}) {
		return await useApi(
			`${endpoints.get('_delete_hapi_catalog_wishlist')}`,
			{
				method: 'DELETE',
				body: {id: options || 0},
			},
			config,
		);
	}

	// fetch wishlist categories
	async function fetchWishlistCategories(options, config = {}) {
		const res = await useApi(`${endpoints.get('_get_hapi_catalog_my_wishlist_categories')}?level_from=${options.level_from}&level_to=${options.level_to}`, null, {appendLang: true, ...config});

		return res;
	}

	// get wishlist products from state
	function getWishlistProducts() {
		return wishlistProducts.value;
	}

	/*
		Add or remove product from wishlist
		{
			shopping_cart_code: '1234567890', // product code
			qty: 1, // 1 to add, 0 to remove
		}
	*/
	async function addRemoveWishlistProducts(options, config = {}) {
		const res = await useApi(
			`${endpoints.get('_post_hapi_catalog_wishlist')}`,
			{
				method: 'POST',
				body: {model: 'catalogproduct', code: options.shopping_cart_code, qty: options.qty},
			},
			config,
		);

		if (res.data?.new_wishlist_data) {
			wishlistProducts.value = res.data.new_wishlist_data;
		}

		return res;
	}

	// fetch compare products
	let comparedProductsTimeout;
	let retryCount = 0;
	async function fetchComparedProducts(options, config = {}) {
		if (comparedProductsTimeout) clearTimeout(comparedProductsTimeout);
		const retryTimeout = options?.retryTimeout || 2000;
		const maxRetries = options?.maxRetries || 5;
		if (retryCount >= maxRetries) return;

		const time = new Date().getTime();
		const res = await useApi(`${endpoints.get('_get_hapi_catalog_compare')}?mode=normal&time=${time}`, null, config);
		const items = res.data?.items?.length ? res.data.items : [];

		// recheck if item is added or removed from compare list (slow server response hack)
		if ((options?.retry == 'remove' && items.find(item => item.id == options.itemId)) || (options?.retry == 'add' && !items.find(item => item.id == options.itemId))) {
			retryCount++;
			return new Promise(resolve => {
				comparedProductsTimeout = setTimeout(async () => {
					resolve(await fetchComparedProducts(options));
				}, retryTimeout);
			});
		}

		comparedProducts.value = items;
		retryCount = 0;
		return res;
	}

	// get compared products from state
	function getComparedProducts() {
		return comparedProducts.value;
	}

	// add to compare
	async function addToCompare(options, config = {}) {
		return await useApi(
			`${endpoints.get('_post_hapi_catalog_compare')}`,
			{
				method: 'POST',
				body: {shopping_cart_code: options.shopping_cart_code},
			},
			config,
		);
	}

	// remove from compare
	async function removeFromCompare(options, config = {}) {
		return await useApi(
			`${endpoints.get('_delete_hapi_catalog_compare')}`,
			{
				method: 'DELETE',
				body: {shopping_cart_code: options.shopping_cart_code},
			},
			config,
		);
	}

	function constructFilterUrl(options) {
		if (!options?.query && !options?.path) return;

		const query = options?.query;
		const searchFields = options?.searchFields;
		const selectedSearchFields = options?.selected;
		let newQuery = {};
		const selectedAttributes = [];
		// Remap old filter url to new filter url structure
		if (__FILTER_URL_MIGRATION__) {
			newQuery = mapFilterUrl({query: options.query, searchFields, selectedSearchFields});
		}

		// ?proizvodac-dizalice-topline=toshiba-estia&vrsta-uredaja=odvlazivac&vrsta-uredaja=ovlazivac&discount=1
		if (__FILTER_URL_TYPE__ == 1) {
			Object.entries(selectedSearchFields.value).forEach(([key, value]) => {
				newQuery[key] = Array.isArray(value) ? value : value ? String(value).split(',') : value;
			});

			// Get all filter_url keys from searchFields
			// Find keys that exist in query but not in selectedSearchFields and remove them
			const allFilterKeys = searchFields.value.map(el => el.filter_url);
			allFilterKeys.forEach(key => {
				if (query[key] && !selectedSearchFields.value[key]) {
					newQuery[key] = undefined;
				}
			});
		}

		// ?a=4822,2159,2160,2778,3460&manufacturers=11,2,7,4,1&discount=1
		if (__FILTER_URL_TYPE__ == 2) {
			// Migrate legacy a_* parameters to standard 'a' parameter
			if (query) {
				const legacyAttributeKeys = Object.keys(query).filter(k => k.startsWith('a_'));
				if (legacyAttributeKeys.length > 0 && !query.a) {
					// Collect all values from legacy a_* parameters
					const legacyValues = legacyAttributeKeys
						.map(k => query[k])
						.filter(v => v)
						.join(',');

					if (legacyValues) {
						// Add to query as standard 'a' parameter
						query.a = legacyValues;
					}

					// Mark legacy parameters for removal
					legacyAttributeKeys.forEach(k => {
						newQuery[k] = undefined;
					});
				}
			}

			// Build params from selected fields
			Object.entries(selectedSearchFields.value).forEach(([key, value]) => {
				const filter = searchFields.value.find(el => el.filter_url == key);
				if (filter?.layout == 'sf') {
					newQuery[filter.filter_url] = Array.isArray(value) ? value[0] : value;
					return;
				}

				const selectedSearchFieldIds = filter.options.filter(el => value.includes(el.filter_url)).map(el => String(el.id));
				if (filter.code.startsWith('a_')) {
					// Combine all selected attributes to one query param
					selectedAttributes.push(...selectedSearchFieldIds);
					newQuery.a = selectedAttributes.length ? selectedAttributes.join(',') : undefined;
				} else {
					// Non-attribute filters (e.g., manufacturers, price, categories)
					newQuery[filter.code] = selectedSearchFieldIds.length ? selectedSearchFieldIds.join(',') : undefined;
				}
			});

			// Cleanup: if something existed in query but has no selection now, unset it
			// 1) Attributes group -> "a"
			const hasAnyAttributeSelected = searchFields.value.some(sf => sf.code?.startsWith('a_') && selectedSearchFields.value[sf.filter_url] && selectedSearchFields.value[sf.filter_url].length);
			if (query.a && !hasAnyAttributeSelected) {
				newQuery.a = undefined;
			}

			// 2) Other codes present in searchFields
			searchFields.value.forEach(sf => {
				if (sf.code && !sf.code.startsWith('a_')) {
					const key = sf.filter_url; // selectedSearchFields key
					const hasSelection = !!(selectedSearchFields.value[key] && selectedSearchFields.value[key].length);
					const queryKey = sf.layout == 'sf' ? sf.filter_url : sf.code;
					if (query[queryKey] && !hasSelection) {
						newQuery[queryKey] = undefined;
					}
				}
			});

			// PHP legacy cleanup: remove empty or zero-valued params
			const combinedKeys = new Set([...Object.keys(query || {}), ...Object.keys(newQuery || {})]);
			combinedKeys.forEach(k => {
				const val = newQuery[k] !== undefined ? newQuery[k] : query[k];
				if (val === '' || val === null || val === undefined || val === 0 || val === '0') {
					newQuery[k] = undefined;
				}
			});
		}

		// /proizvodi/kozmetika/brand:almagea,alphanova;muci-me:priprema-koze-za-sunce;mjesto-primjene:koza-tijela/?search_q=
		if (__FILTER_URL_TYPE__ == 3) {
			const filterSegments = [];

			// Build filter segments from selected fields
			Object.entries(selectedSearchFields.value).forEach(([key, value]) => {
				if (value?.length) {
					const filterUrlKey = key;
					const filterSlugs = value.join(',');
					filterSegments.push(`${filterUrlKey}:${filterSlugs}`);
				}
			});

			// Construct the new filter URL segment
			const path = getUrlSegments(options.path, {ignoreLang: true, removeLast: options.path.includes(':') ? true : false, stringify: true, addSlashes: true});
			const filterUrlSegment = filterSegments.length ? filterSegments.join(';') : '';
			newQuery.url = path + filterUrlSegment;
		}

		return newQuery;
	}

	function mapFilterUrl(options) {
		const query = options?.query;
		const searchFields = options?.searchFields?.value;
		if (!query || !searchFields) return;

		const newAttributes = {};

		// Migration from filterUrlType 2 to filterUrlType 1 (a=1182,1185 -> foo=bar&foo2=bar2)
		if (__FILTER_URL_MIGRATION__ == '2>1') {
			// Helper function to map IDs to filter URLs
			function mapIdsToFilterUrls(ids, fields) {
				ids.forEach(id => {
					fields.forEach(field => {
						if (field.options && Array.isArray(field.options)) {
							const matchedOption = field.options.find(opt => String(opt.id) === String(id));

							if (matchedOption && field.filter_url && matchedOption.filter_url) {
								if (!newAttributes[field.filter_url]) {
									newAttributes[field.filter_url] = [];
								}
								if (!newAttributes[field.filter_url].includes(matchedOption.filter_url)) {
									newAttributes[field.filter_url].push(matchedOption.filter_url);
								}
							}
						}
					});
				});
			}

			// Process "a" query parameter (attributes)
			if (query?.a) {
				const attributeIds = query.a
					.split(',')
					.map(id => id.trim())
					.filter(id => id);
				// Filter out manufacturers, price, and categories filters to avoid conflicts with regular attributes
				const filteredSearchFields = searchFields.filter(field => !['manufacturers', 'price', 'categories'].includes(field.code));
				mapIdsToFilterUrls(attributeIds, filteredSearchFields);
				newAttributes.a = undefined;
			}

			// Process manufacturers query parameter
			if (query?.manufacturers) {
				const manufacturerIds = query.manufacturers
					.split(',')
					.map(id => id.trim())
					.filter(id => id);
				const manufacturersField = searchFields.find(field => field.code === 'manufacturers');
				if (manufacturersField) {
					mapIdsToFilterUrls(manufacturerIds, [manufacturersField]);
				}
				newAttributes.manufacturers = undefined;
			}

			// Process categories query parameter
			if (query?.categories) {
				const categoryIds = query.categories
					.split(',')
					.map(id => id.trim())
					.filter(id => id);
				const categoriesField = searchFields.find(field => field.code === 'categories');
				if (categoriesField) {
					mapIdsToFilterUrls(categoryIds, [categoriesField]);
				}
				newAttributes.categories = undefined;
			}

			// Process categories query parameter
			if (query?.price) {
				const priceIds = query.price
					.split(',')
					.map(id => id.trim())
					.filter(id => id);
				const priceField = searchFields.find(field => field.code === 'price');
				if (priceField) {
					mapIdsToFilterUrls(priceIds, [priceField]);
				}
				newAttributes.price = undefined;
			}

			// Remove all query params that have value 0 or are empty
			Object.keys(query).forEach(key => {
				if (query[key] === 0 || query[key] === '0' || query[key] === '' || query[key] === null) {
					newAttributes[key] = undefined;
				}
			});

			// Remove other unused query params
			newAttributes.searchcode = undefined;
			newAttributes.searchid = undefined;
			newAttributes.formvalidator = undefined;
		}

		return newAttributes;
	}

	function isCatalogRoot(url) {
		const removeLast = __FILTER_URL_TYPE__ == 3 && url.includes(':');
		const u = getUrlSegments(url, {ignoreLang: true, removeLast: removeLast});
		return u?.length == 1 && ['proizvodi', 'products', 'izdelki', 'produkte', 'prodotti', 'producto'].includes(u[0]) && !route.query.search_q;
	}

	function getCatalogLangBasePath(lang) {
		let paths = {
			hr: {
				items: 'proizvodi',
				item: 'proizvod',
			},
			ba: {
				items: 'proizvodi',
				item: 'proizvod',
			},
			si: {
				items: 'izdelki',
				item: 'izdelek',
			},
			en: {
				items: 'products',
				item: 'product',
			},
			de: {
				items: 'produkte',
				item: 'product',
			},
			at: {
				items: 'produkte',
				item: 'product',
			},
			it: {
				items: 'prodotti',
				item: 'prodotto',
			},
			es: {
				items: 'productos',
				item: 'producto',
			},
		};

		if (appConfig?.catalog?.urls) {
			paths = {...paths, ...appConfig.catalog.urls};
		}

		return lang ? paths[lang] : paths;
	}

	return {
		category,
		fetchCategoryBySlug,
		fetchCategories,
		fetchProducts,
		fetchProductsPriceQty,
		updateProductsPriceQty,
		fetchProduct,
		fetchLists,
		fetchSellers,
		fetchManufacturers,
		removeWishlistProducts,
		fetchWishlistProducts,
		fetchWishlistCategories,
		getWishlistProducts,
		addRemoveWishlistProducts,
		comparedProducts,
		fetchComparedProducts,
		getComparedProducts,
		addToCompare,
		removeFromCompare,
		fetchListBySlug,
		constructFilterUrl,
		isCatalogRoot,
		getCatalogLangBasePath,
		getProductDetail,
	};
}
